import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
  FormControlLabel,
  IconButton,
  Divider,
  Button,
  Paper,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useDroppable } from "@dnd-kit/core";
import { collectionFields, getDerived, recordListField, RECORD_LIST_PATH } from "../fieldCatalog";

const label = (t) => (
  <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
    {t}
  </Typography>
);

const Hint = ({ children }) => (
  <Paper variant="outlined" sx={{ p: 1, mt: 1, bgcolor: "action.hover", borderStyle: "dashed" }}>
    <Typography variant="caption" color="text.secondary">
      {children}
    </Typography>
  </Paper>
);

function SheetSettings({ sheet, dispatch }) {
  const set = (patch) => dispatch({ type: "SET_SHEET", patch });
  return (
    <Box>
      {label("Sheet settings")}
      <TextField
        fullWidth
        size="small"
        label="Sheet name"
        value={(sheet && sheet.sheetName) || ""}
        onChange={(e) => set({ sheetName: e.target.value })}
        sx={{ mb: 1.5 }}
      />
      <TextField
        fullWidth
        size="small"
        label="Number / currency format"
        value={(sheet && sheet.currencyFormat) || ""}
        onChange={(e) => set({ currencyFormat: e.target.value })}
        sx={{ mb: 1.5 }}
      />
      <TextField
        fullWidth
        size="small"
        label="Date format"
        value={(sheet && sheet.dateFormat) || ""}
        onChange={(e) => set({ dateFormat: e.target.value })}
      />
      <Hint>Nothing selected. Click a section on the sheet to edit it, or drag fields from the left to map data.</Hint>
    </Box>
  );
}

function RowItem({ primary, secondary, onDelete }) {
  return (
    <Paper variant="outlined" sx={{ p: 1, display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700} noWrap>
          {primary}
        </Typography>
        {secondary && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
            {secondary}
          </Typography>
        )}
      </Box>
      <IconButton size="small" onClick={onDelete}>
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Paper>
  );
}

export default function PropertiesPanel({ template, section, dispatch, formulaAppendRef }) {
  if (!section) return <SheetSettings sheet={template.sheet} dispatch={dispatch} />;

  const sec = section;
  const patchSection = (patch) => dispatch({ type: "UPDATE_SECTION", id: sec.id, patch });
  const patchStyle = (stylePatch) => patchSection({ style: { ...(sec.style || {}), ...stylePatch } });

  const titleMap = {
    TITLE: "Title",
    DETAIL_FIELDS: "Detail fields",
    DATA_TABLE: "Data table",
    FOOTER_TOTALS: "Totals",
    TEXT: "Text note",
    SPACER: "Spacer",
  };

  return (
    <Box>
      {label(`${titleMap[sec.type]} properties`)}

      {(sec.type === "TITLE" || sec.type === "TEXT") && (
        <>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Text ( {{path}} inserts data )"
            value={sec.text || ""}
            onChange={(e) => patchSection({ text: e.target.value })}
          />
          {sec.type === "TITLE" && (
            <Box sx={{ mt: 1.5 }}>
              {label("Alignment")}
              <ToggleButtonGroup
                size="small"
                exclusive
                value={(sec.style && sec.style.align) || "center"}
                onChange={(_, v) => v && patchStyle({ align: v })}
              >
                <ToggleButton value="left">Left</ToggleButton>
                <ToggleButton value="center">Center</ToggleButton>
                <ToggleButton value="right">Right</ToggleButton>
              </ToggleButtonGroup>
              <FormControlLabel
                sx={{ display: "block", mt: 1 }}
                control={
                  <Switch
                    checked={!!(sec.style && sec.style.bold)}
                    onChange={(e) => patchStyle({ bold: e.target.checked })}
                  />
                }
                label="Bold"
              />
            </Box>
          )}
          <Hint>
            Example: <code>#{"{{orderId}}"}</code> pulls the live value into the text.
          </Hint>
        </>
      )}

      {sec.type === "DETAIL_FIELDS" && (
        <>
          {label("Columns per row")}
          <ToggleButtonGroup
            size="small"
            exclusive
            value={sec.columnsPerRow || 2}
            onChange={(_, v) => v && patchSection({ columnsPerRow: v })}
          >
            <ToggleButton value={1}>1</ToggleButton>
            <ToggleButton value={2}>2</ToggleButton>
          </ToggleButtonGroup>

          <Divider sx={{ my: 1.5 }} />
          {label("Mapped fields")}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {(sec.fields || []).length === 0 && (
              <Typography variant="caption" color="text.disabled">
                None yet — drag fields onto this section.
              </Typography>
            )}
            {(sec.fields || []).map((fl, i) => (
              <RowItem
                key={i}
                primary={fl.label}
                secondary={fl.path}
                onDelete={() => dispatch({ type: "REMOVE_FIELD", id: sec.id, index: i })}
              />
            ))}
          </Box>
          <Hint>Drag a field chip from the left onto this section to map it.</Hint>
        </>
      )}

      {sec.type === "DATA_TABLE" && (
        <DataTableProps sec={sec} source={template.source} dispatch={dispatch} patchSection={patchSection} formulaAppendRef={formulaAppendRef} />
      )}

      {sec.type === "FOOTER_TOTALS" && <FooterProps sec={sec} source={template.source} dispatch={dispatch} />}

      {sec.type === "SPACER" && (
        <TextField
          type="number"
          size="small"
          label="Blank rows"
          value={sec.rows || 1}
          onChange={(e) => patchSection({ rows: Math.max(1, Number(e.target.value) || 1) })}
          inputProps={{ min: 1 }}
        />
      )}

      <Divider sx={{ my: 2 }} />
      <Button
        fullWidth
        color="error"
        variant="outlined"
        startIcon={<DeleteOutlineIcon />}
        onClick={() => dispatch({ type: "REMOVE_SECTION", id: sec.id })}
      >
        Delete section
      </Button>
    </Box>
  );
}

function DataTableProps({ sec, source, dispatch, patchSection, formulaAppendRef }) {
  const boundCollection =
    sec.collectionPath === RECORD_LIST_PATH
      ? recordListField(source)
      : collectionFields(source).find((c) => c.path === sec.collectionPath);
  const filterableChildren = (boundCollection && boundCollection.children ? boundCollection.children : []).filter(
    (ch) => ch.type === "string" || ch.type === "enum"
  );

  const setFilter = (patch) => {
    const next = { ...(sec.rowFilter || { op: "eq", path: "", value: "" }), ...patch };
    patchSection({ rowFilter: next.path ? next : null });
  };

  return (
    <>
      <TextField
        fullWidth
        size="small"
        label="Repeats over"
        value={
          !sec.collectionPath || sec.collectionPath === RECORD_LIST_PATH ? "One row per record" : sec.collectionPath
        }
        InputProps={{ readOnly: true }}
        sx={{ mb: 1.5 }}
      />

      {label("Row filter (optional)")}
      <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
        <TextField
          select
          size="small"
          label="Field"
          value={(sec.rowFilter && sec.rowFilter.path) || ""}
          onChange={(e) => setFilter({ path: e.target.value })}
          sx={{ flex: 1 }}
        >
          <MenuItem value="">
            <em>none</em>
          </MenuItem>
          {filterableChildren.map((ch) => (
            <MenuItem key={ch.path} value={ch.path}>
              {ch.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Equals"
          value={(sec.rowFilter && sec.rowFilter.value) || ""}
          onChange={(e) => setFilter({ value: e.target.value })}
          disabled={!(sec.rowFilter && sec.rowFilter.path)}
          sx={{ flex: 1 }}
        />
      </Box>

      <FormControlLabel
        control={<Switch checked={!!sec.showColumnTotals} onChange={(e) => patchSection({ showColumnTotals: e.target.checked })} />}
        label="Show column totals"
      />
      <FormControlLabel
        control={<Switch checked={!!sec.zebra} onChange={(e) => patchSection({ zebra: e.target.checked })} />}
        label="Zebra rows"
      />

      <Divider sx={{ my: 1.5 }} />
      {label("Columns")}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {(sec.columns || []).length === 0 && (
          <Typography variant="caption" color="text.disabled">
            Drag a collection's fields onto the table.
          </Typography>
        )}
        {(sec.columns || []).map((c, i) => (
          <Paper key={i} variant="outlined" sx={{ p: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                size="small"
                variant="standard"
                value={c.header}
                onChange={(e) => dispatch({ type: "UPDATE_COLUMN", id: sec.id, index: i, patch: { header: e.target.value } })}
                sx={{ flex: 1 }}
              />
              <IconButton
                size="small"
                disabled={i === 0}
                onClick={() => dispatch({ type: "MOVE_COLUMN", id: sec.id, index: i, dir: -1 })}
                title="Move up"
              >
                <KeyboardArrowUpIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={i === (sec.columns || []).length - 1}
                onClick={() => dispatch({ type: "MOVE_COLUMN", id: sec.id, index: i, dir: 1 })}
                title="Move down"
              >
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => dispatch({ type: "REMOVE_COLUMN", id: sec.id, index: i })} title="Remove">
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={c.align || "left"}
                onChange={(_, v) => v && dispatch({ type: "UPDATE_COLUMN", id: sec.id, index: i, patch: { align: v } })}
              >
                <ToggleButton value="left" sx={{ py: 0.1, px: 1 }}>
                  L
                </ToggleButton>
                <ToggleButton value="right" sx={{ py: 0.1, px: 1 }}>
                  R
                </ToggleButton>
              </ToggleButtonGroup>
              <FormControlLabel
                sx={{ ml: 0.5 }}
                control={
                  <Switch
                    size="small"
                    checked={!!c.total}
                    onChange={(e) => dispatch({ type: "UPDATE_COLUMN", id: sec.id, index: i, patch: { total: e.target.checked } })}
                  />
                }
                label={<Typography variant="caption">Total</Typography>}
              />
              <Typography variant="caption" color="text.disabled" sx={{ ml: "auto" }}>
                {c.type} · {c.level === "PARENT" ? "record" : (c.level || "").replace(/\[\]$/, "") || "row"}
              </Typography>
            </Box>
            {c.computed && (
              <Typography variant="caption" sx={{ display: "block", mt: 0.5, fontFamily: "monospace", color: "info.main" }}>
                = {c.computed}
              </Typography>
            )}
          </Paper>
        ))}
      </Box>

      <Divider sx={{ my: 1.5 }} />
      {label("Add computed column")}
      <ComputedColumnForm sec={sec} boundCollection={boundCollection} dispatch={dispatch} formulaAppendRef={formulaAppendRef} />
    </>
  );
}

const OPS = [
  ["+", "+"],
  ["−", "-"],
  ["×", "*"],
  ["÷", "/"],
  ["(", "("],
  [")", ")"],
];

function ComputedColumnForm({ sec, boundCollection, dispatch, formulaAppendRef }) {
  const [header, setHeader] = useState("");
  const [formula, setFormula] = useState("");
  const [type, setType] = useState("currency");
  const { setNodeRef, isOver } = useDroppable({ id: "formula-drop", data: { kind: "FORMULA_DROP" } });
  const fields = (boundCollection && boundCollection.children ? boundCollection.children : []).map((c) => c.path);

  const append = (tok) => setFormula((f) => (f && f.trim() ? `${f.trim()} ${tok}` : tok));
  const backspace = () => setFormula((f) => f.trim().split(/\s+/).slice(0, -1).join(" "));

  // Let the page route dropped field chips into this formula.
  useEffect(() => {
    if (!formulaAppendRef) return undefined;
    formulaAppendRef.current = (field) => append(field.path);
    return () => {
      formulaAppendRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formulaAppendRef]);

  const add = () => {
    if (!header.trim() || !formula.trim()) return;
    dispatch({
      type: "ADD_COLUMN",
      id: sec.id,
      column: { header: header.trim(), computed: formula.trim(), type, align: "right", total: true },
    });
    setHeader("");
    setFormula("");
  };

  return (
    <Box>
      <TextField size="small" fullWidth label="Column name" value={header} onChange={(e) => setHeader(e.target.value)} sx={{ mb: 1 }} />

      <Box
        ref={setNodeRef}
        sx={{
          border: "1px dashed",
          borderColor: isOver ? "info.main" : "divider",
          bgcolor: isOver ? "action.hover" : "background.paper",
          borderRadius: 1,
          p: 1,
          minHeight: 42,
          fontFamily: "monospace",
          fontSize: 13,
          wordBreak: "break-word",
        }}
      >
        {formula || (
          <Typography variant="caption" color="text.disabled">
            Drag fields here · tap operators below
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
        {OPS.map(([lbl, op]) => (
          <Button key={op} size="small" variant="outlined" onClick={() => append(op)} sx={{ minWidth: 34, px: 1 }}>
            {lbl}
          </Button>
        ))}
        <Button size="small" variant="outlined" onClick={backspace} sx={{ minWidth: 34, px: 1 }}>
          ⌫
        </Button>
        <Button size="small" color="error" onClick={() => setFormula("")}>
          Clear
        </Button>
      </Box>

      <NumberInsert onInsert={append} />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
        <ToggleButtonGroup size="small" exclusive value={type} onChange={(_, v) => v && setType(v)}>
          <ToggleButton value="currency" sx={{ py: 0.2, px: 1 }}>
            Currency
          </ToggleButton>
          <ToggleButton value="number" sx={{ py: 0.2, px: 1 }}>
            Number
          </ToggleButton>
        </ToggleButtonGroup>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={add}
          disabled={!header.trim() || !formula.trim()}
          sx={{ ml: "auto" }}
        >
          Add
        </Button>
      </Box>

      <Hint>
        Drag a field into the box to insert its exact name, then tap an operator — e.g. <code>cost − paidAmount</code>. The
        total sums the result.
        {fields.length ? ` Fields: ${fields.slice(0, 6).join(", ")}${fields.length > 6 ? " …" : ""}` : ""}
      </Hint>
    </Box>
  );
}

function NumberInsert({ onInsert }) {
  const [n, setN] = useState("");
  return (
    <Box sx={{ display: "flex", gap: 0.5, mt: 1 }}>
      <TextField
        size="small"
        type="number"
        placeholder="number"
        value={n}
        onChange={(e) => setN(e.target.value)}
        sx={{ width: 120 }}
      />
      <Button
        size="small"
        variant="outlined"
        disabled={n === ""}
        onClick={() => {
          onInsert(String(n));
          setN("");
        }}
      >
        Insert #
      </Button>
    </Box>
  );
}

function FooterProps({ sec, source, dispatch }) {
  const derived = getDerived(source);
  const usedDerived = new Set((sec.rows || []).filter((r) => r.derived).map((r) => r.derived));
  const availableDerived = Object.keys(derived).filter((k) => !usedDerived.has(k));

  return (
    <>
      {label("Summary rows")}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {(sec.rows || []).length === 0 && (
          <Typography variant="caption" color="text.disabled">
            Drag numeric fields here, or add a derived total below.
          </Typography>
        )}
        {(sec.rows || []).map((r, i) => (
          <RowItem
            key={i}
            primary={r.label}
            secondary={r.derived ? `derived: ${r.derived}` : r.path}
            onDelete={() => dispatch({ type: "REMOVE_FOOTER_ROW", id: sec.id, index: i })}
          />
        ))}
      </Box>

      {availableDerived.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          {label("Add derived total")}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {availableDerived.map((k) => (
              <Button
                key={k}
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() =>
                  dispatch({
                    type: "ADD_FOOTER_ROW",
                    id: sec.id,
                    row: { label: derived[k].label, derived: k, type: "currency", style: { bold: true } },
                  })
                }
              >
                {derived[k].label}
              </Button>
            ))}
          </Box>
        </Box>
      )}
      <Hint>Drag a numeric field onto this section, or add a built-in derived total (e.g. Balance = cost − paid).</Hint>
    </>
  );
}
